import { t } from "../i18n/index.ts";
import type { IconName } from "./icons.js";
import { normalizeLowercaseStringOrEmpty } from "./string-coerce.ts";

export const TAB_GROUPS = [
  { label: "chat", tabs: ["chat"] },
  {
    label: "control",
    tabs: ["overview", "channels", "instances", "sessions", "usage", "cron", "integrations", "activity"],
  },
  { label: "agent", tabs: ["agents", "packs", "skills", "nodes", "dreams"] },
  {
    label: "settings",
    tabs: [
      "config",
      "communications",
      "appearance",
      "automation",
      "infrastructure",
      "aiAgents",
      "debug",
      "logs",
    ],
  },
] as const;

/**
 * Tabs visible in managed/SaaS mode (e.g. ALITA).
 * Everything NOT in this set is hidden. Allowlist approach ensures new upstream
 * tabs are hidden by default — you explicitly opt them in.
 */
export const MANAGED_VISIBLE_TABS: ReadonlySet<Tab> = new Set<Tab>([
  "chat",
  "packs",
  "cron",
  "skills",
  "channels",
  "appearance",
  "integrations",
  "activity",
]);

/**
 * Tabs that can be browsed WITHOUT authentication in managed mode.
 * The UI renders these tabs even when the user hasn't signed in.
 * Actions within these tabs (Send, Install, Connect) still gate behind auth.
 */
export const MANAGED_PUBLIC_TABS: ReadonlySet<Tab> = new Set<Tab>([
  "chat",
  "packs",
  "appearance",
]);

/**
 * Tabs that require authentication to access in managed mode.
 * These contain personalized data that only makes sense with a connected pod.
 */
export const MANAGED_AUTH_REQUIRED_TABS: ReadonlySet<Tab> = new Set<Tab>([
  "skills",
  "integrations",
  "cron",
  "channels",
  "activity",
]);

/** Returns tab groups filtered for managed mode. Empty groups are excluded.
 *  When `connected` is false, auth-required tabs are also hidden. */
export function getVisibleTabGroups(
  managedMode: boolean,
  connected = true,
): readonly { label: string; tabs: readonly Tab[] }[] {
  if (!managedMode) {
    return TAB_GROUPS;
  }
  return (TAB_GROUPS as readonly { label: string; tabs: readonly Tab[] }[])
    .map((g) => ({
      ...g,
      tabs: g.tabs.filter((t) =>
        MANAGED_VISIBLE_TABS.has(t) && (connected || !MANAGED_AUTH_REQUIRED_TABS.has(t))
      ),
    }))
    .filter((g) => g.tabs.length > 0);
}

export type Tab =
  | "agents"
  | "overview"
  | "channels"
  | "instances"
  | "sessions"
  | "usage"
  | "cron"
  | "skills"
  | "nodes"
  | "chat"
  | "config"
  | "communications"
  | "appearance"
  | "automation"
  | "infrastructure"
  | "aiAgents"
  | "debug"
  | "logs"
  | "dreams"
  | "integrations"
  | "activity"
  | "packs";

const TAB_PATHS: Record<Tab, string> = {
  agents: "/agents",
  overview: "/overview",
  channels: "/channels",
  instances: "/instances",
  sessions: "/sessions",
  usage: "/usage",
  cron: "/cron",
  skills: "/skills",
  nodes: "/nodes",
  chat: "/chat",
  config: "/config",
  communications: "/communications",
  appearance: "/appearance",
  automation: "/automation",
  infrastructure: "/infrastructure",
  aiAgents: "/ai-agents",
  debug: "/debug",
  logs: "/logs",
  dreams: "/dreaming",
  integrations: "/integrations",
  activity: "/activity",
  packs: "/packs",
};

const PATH_ALIASES: Record<string, Tab> = {
  "/dreams": "dreams",
};

const PATH_TO_TAB = new Map<string, Tab>([
  ...Object.entries(TAB_PATHS).map(([tab, path]) => [path, tab as Tab] as const),
  ...Object.entries(PATH_ALIASES),
]);

export function normalizeBasePath(basePath: string): string {
  if (!basePath) {
    return "";
  }
  let base = basePath.trim();
  if (!base.startsWith("/")) {
    base = `/${base}`;
  }
  if (base === "/") {
    return "";
  }
  if (base.endsWith("/")) {
    base = base.slice(0, -1);
  }
  return base;
}

export function normalizePath(path: string): string {
  if (!path) {
    return "/";
  }
  let normalized = path.trim();
  if (!normalized.startsWith("/")) {
    normalized = `/${normalized}`;
  }
  if (normalized.length > 1 && normalized.endsWith("/")) {
    normalized = normalized.slice(0, -1);
  }
  return normalized;
}

export function pathForTab(tab: Tab, basePath = ""): string {
  const base = normalizeBasePath(basePath);
  const path = TAB_PATHS[tab];
  return base ? `${base}${path}` : path;
}

export function tabFromPath(pathname: string, basePath = ""): Tab | null {
  const base = normalizeBasePath(basePath);
  let path = pathname || "/";
  if (base) {
    if (path === base) {
      path = "/";
    } else if (path.startsWith(`${base}/`)) {
      path = path.slice(base.length);
    }
  }
  let normalized = normalizeLowercaseStringOrEmpty(normalizePath(path));
  if (normalized.endsWith("/index.html")) {
    normalized = "/";
  }
  if (normalized === "/") {
    return "chat";
  }
  return PATH_TO_TAB.get(normalized) ?? null;
}

export function inferBasePathFromPathname(pathname: string): string {
  let normalized = normalizePath(pathname);
  if (normalized.endsWith("/index.html")) {
    normalized = normalizePath(normalized.slice(0, -"/index.html".length));
  }
  if (normalized === "/") {
    return "";
  }
  const segments = normalized.split("/").filter(Boolean);
  if (segments.length === 0) {
    return "";
  }
  for (let i = 0; i < segments.length; i++) {
    const candidate = normalizeLowercaseStringOrEmpty(`/${segments.slice(i).join("/")}`);
    if (PATH_TO_TAB.has(candidate)) {
      const prefix = segments.slice(0, i);
      return prefix.length ? `/${prefix.join("/")}` : "";
    }
  }
  return `/${segments.join("/")}`;
}

export function iconForTab(tab: Tab): IconName {
  switch (tab) {
    case "agents":
      return "folder";
    case "chat":
      return "messageSquare";
    case "overview":
      return "barChart";
    case "channels":
      return "link";
    case "instances":
      return "radio";
    case "sessions":
      return "fileText";
    case "usage":
      return "barChart";
    case "cron":
      return "loader";
    case "skills":
      return "zap";
    case "nodes":
      return "monitor";
    case "config":
      return "settings";
    case "communications":
      return "send";
    case "appearance":
      return "spark";
    case "automation":
      return "terminal";
    case "infrastructure":
      return "globe";
    case "aiAgents":
      return "brain";
    case "debug":
      return "bug";
    case "logs":
      return "scrollText";
    case "dreams":
      return "moon";
    case "integrations":
      return "link";
    case "activity":
      return "loader";
    case "packs":
      return "zap";
    default:
      return "folder";
  }
}

/**
 * Managed-mode friendly tab names. Non-technical users see these instead of
 * the developer-oriented defaults. Self-hosted users still get the i18n keys.
 */
const MANAGED_TAB_TITLES: Partial<Record<Tab, string>> = {
  chat: "Chat",
  cron: "Automations",
  channels: "Connections",
  integrations: "Apps",
  activity: "History",
  skills: "Skills",
  appearance: "Theme",
  packs: "Skill Packs",
};

/** Managed-mode friendly group labels. */
const MANAGED_GROUP_LABELS: Record<string, string> = {
  chat: "",
  control: "Manage",
  agent: "Explore",
  settings: "Preferences",
};

export function titleForTab(tab: Tab, managedMode = false) {
  if (managedMode && MANAGED_TAB_TITLES[tab]) return MANAGED_TAB_TITLES[tab]!;
  return t(`tabs.${tab}`);
}

export function labelForGroup(label: string, managedMode = false): string {
  if (managedMode && label in MANAGED_GROUP_LABELS) return MANAGED_GROUP_LABELS[label];
  return t(`nav.${label}`);
}

const MANAGED_TAB_SUBTITLES: Partial<Record<Tab, string>> = {
  channels: "Connect ALITA to your favorite messaging apps.",
  cron: "Set up recurring tasks and reminders.",
  integrations: "Connect your apps so ALITA can take action.",
  activity: "See what ALITA has been doing.",
  skills: "Teach ALITA new routines and workflows.",
  appearance: "Customize your experience.",
  packs: "Ready-made skill bundles for every role.",
};

export function subtitleForTab(tab: Tab, managedMode = false) {
  if (managedMode && MANAGED_TAB_SUBTITLES[tab]) return MANAGED_TAB_SUBTITLES[tab]!;
  return t(`subtitles.${tab}`);
}
